import { logger } from '@/lib/logger';

/**
 * Local-proxy only: set the `oauth_slug` cookie that the shared oauth-gateway
 * (`local.ahoylocal.ai`) reads to route an external OAuth callback back to the
 * correct per-branch instance.
 *
 * In local-proxy, custom OAuth flows (Fathom / Granola / Fireflies) register a
 * single stable redirect URI on the shared gateway host. The gateway needs this
 * cookie to know which `api-{slug}.ahoylocal.ai` instance should handle the
 * callback. Without it the gateway returns "No instance cookie found".
 *
 * Call this immediately before redirecting the browser to an external OAuth
 * provider. The cookie is short-lived (10 minutes) and scoped to
 * `.ahoylocal.ai` so it survives the cross-subdomain callback.
 *
 * This mirrors the cookie logic in `allauth.js` `redirectToProvider` (the
 * canonical allauth social-login path). It is a deliberate no-op on production
 * and any non-`*.ahoylocal.ai` host — production uses real per-instance redirect
 * URIs with no gateway, so no cookie is needed.
 */
export function setOAuthInstanceCookie(): void {
  const { hostname } = window.location;

  // Matches legacy ({number}.local.ahoylocal.ai) and slug ({slug}.ahoylocal.ai)
  // local-proxy hosts. Same patterns as allauth.js redirectToProvider.
  const legacyMatch = hostname.match(/^(\d+)\.local\.ahoylocal\.ai$/);
  const slugMatch =
    hostname.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.ahoylocal\.ai$/) ||
    hostname.match(/^([a-z0-9])\.ahoylocal\.ai$/);

  const match = legacyMatch ?? slugMatch;
  if (!match || hostname === 'local.ahoylocal.ai') {
    // Production / non-proxy host (or the gateway's own base domain): no
    // per-instance routing needed, nothing to do.
    return;
  }

  const slug = match[1];
  try {
    // The `api-` prefix tells the gateway to route to the API subdomain
    // (api-{slug}.ahoylocal.ai) where Django handles the callback, not the
    // frontend subdomain ({slug}.ahoylocal.ai) where Vite runs.
    const cookieSlug = `api-${slug}`;
    document.cookie = `oauth_slug=${cookieSlug}; domain=.ahoylocal.ai; path=/; secure; samesite=lax; max-age=600`;
    logger.debug('[OAuth] Set oauth_slug cookie for instance routing', {
      cookieSlug,
    });
  } catch (error) {
    logger.error(
      '[OAuth] Failed to set oauth_slug cookie',
      error instanceof Error ? error : new Error(String(error))
    );
  }
}
